/**
 * OrphanPanel component for Infrastructure Map
 *
 * Side panel listing unlinked endpoints (orphans) that can be dragged
 * to the canvas or linked to existing nodes.
 */

import { useMemo, useState, type ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import type { OrphanPanelProps } from '../../../types/infrastructure-map'
import {
  EmptyOrphanPanel,
  OrphanItem,
  PanelFooter,
  PanelHeader,
  PanelInstructions,
} from './orphan-panel/orphan-panel.parts'

const noopDragStart = (_endpointId: string): void => {}

export function OrphanPanel({ orphanEndpoints, onAddToMap }: OrphanPanelProps): ReactElement {
  const { t } = useTranslation('cases')
  const [searchQuery, setSearchQuery] = useState('')

  const filteredOrphans = useMemo(() => {
    if (!searchQuery.trim()) return orphanEndpoints
    const query = searchQuery.toLowerCase()
    return orphanEndpoints.filter(
      (endpoint) =>
        endpoint.hostname.toLowerCase().includes(query) ||
        endpoint.ipAddress.toLowerCase().includes(query) ||
        endpoint.deviceType.toLowerCase().includes(query)
    )
  }, [orphanEndpoints, searchQuery])

  if (orphanEndpoints.length === 0) return <EmptyOrphanPanel t={t} />

  return (
    <div className="flex w-64 flex-col border-l border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-900/50">
      <PanelHeader
        totalCount={orphanEndpoints.length}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        t={t}
      />
      <PanelInstructions t={t} />
      <div className="flex-1 space-y-0.5 overflow-y-auto p-2">
        {filteredOrphans.length === 0 ? (
          <p className="py-4 text-center text-sm text-slate-500 dark:text-slate-400">
            {t('infrastructure.canvas.noMatchingOrphans')}
          </p>
        ) : (
          filteredOrphans.map((endpoint) => (
            <OrphanItem
              key={endpoint.id}
              id={endpoint.id}
              hostname={endpoint.hostname}
              ipAddress={endpoint.ipAddress}
              deviceType={endpoint.deviceType}
              onDragStart={noopDragStart}
              onAddToMap={onAddToMap}
              t={t}
            />
          ))
        )}
      </div>
      <PanelFooter
        filteredCount={filteredOrphans.length}
        totalCount={orphanEndpoints.length}
        t={t}
      />
    </div>
  )
}

export default OrphanPanel
